import { useCallback, useContext, useMemo } from 'react';
import { PerProjectCostCodeNameContext } from 'app/hooks/usePerProjectCostCodeNames';
import { useAppSelector } from 'app/store';
import { useTranslation } from 'react-i18next';
import { Box, Typography } from '@mui/material';
import TableDropdown, { TableDropdownOption } from 'app/shared-components/TableDropdown/TableDropdown';
import { selectAgaveSlice } from 'app/pages/store/agaveSlice';
import { selectUserCompanies } from 'app/store/userCompaniesSlice';
import { useAgaveCodingDropdown } from 'app/hooks/useAgaveCodingDropdown';
import { useFlexCodingDropdown } from 'app/hooks/useFlexCodingDropdown';
import { defaultIdentifierOrder, useGetOptionLabel } from 'app/hooks/useGetOptionLabel';
import { isCodingSortedByCode } from 'util/codingSortSetting';
import { CODING_SORT_FIELDS } from 'util/sortAlphanumericArrays';
import {
  AgaveProject,
  AgaveGlCode,
  AgaveCostCode,
  AgaveCostType,
  AgaveVendor,
  AgaveLocation,
  AgaveDepartment,
  AgaveSubJob,
  AgaveEmployeeEntity,
  AgaveEquipment,
  AgaveEmCostCode,
  AgaveEmCostType,
  AgaveSubaccount,
  FlexGlCode,
  FlexCode,
  FlexCostCode,
  FlexCostType,
  FlexVendor,
} from 'API';

// Define supported entity types
export type EntityType =
  | 'vendors'
  | 'locations'
  | 'departments'
  | 'subaccounts'
  | 'projects'
  | 'glCodes'
  | 'costCodes'
  | 'costTypes'
  | 'subJobs'
  | 'employees'
  | 'equipment'
  | 'emCostCodes'
  | 'emCostTypes';

export type AgaveEntity =
  | AgaveProject
  | AgaveGlCode
  | AgaveCostCode
  | AgaveCostType
  | AgaveVendor
  | AgaveLocation
  | AgaveDepartment
  | AgaveSubaccount
  | AgaveSubJob
  | AgaveEmployeeEntity
  | AgaveEquipment
  | AgaveEmCostCode
  | AgaveEmCostType;

export type FlexEntity = FlexGlCode | FlexCode | FlexCostCode | FlexCostType | FlexVendor;

export type CodingEntity = AgaveEntity | FlexEntity;

// Default name fields per entity type for Flex
const FLEX_NAME_FIELDS: Partial<Record<EntityType, string>> = {
  glCodes: 'name',
  projects: 'projectName',
  costCodes: 'code',
  costTypes: 'name',
  vendors: 'name',
};

interface CodingEntityDropdownProps<T extends CodingEntity> {
  entityType: EntityType;
  selectedItem?: T | null;
  onValueChange: (option: T | null) => void;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  disableHeader?: boolean;
  identifierField?: keyof T;
  dataDropdownType?: string;
  // Agave dependency props for hierarchical filtering
  cardGroupId?: string;
  projectId?: string;
  costCodeId?: string;
  subJobId?: string;
  locationId?: string;
  departmentId?: string;
  employeeId?: string;
  // SEE-1325: EM cascade — emGroup/emCo/department come off the selected
  // equipment, emCostCodeId off the selected EM cost code.
  emGroup?: string;
  emCo?: string;
  department?: string;
  emCostCodeId?: string;
  // Flex dependency props
  selectedGlCode?: { id: string } | null;
  selectedProject?: { id: string } | null;
  enabled?: boolean;
  /** When true, options are loaded only when dropdown opens (for filter-dependent types in table rows). */
  fetchOnlyOnOpen?: boolean;
  // Label formatting
  includeDescription?: boolean;
  overrideItems?: T[] | null;
  /** When 'flex', uses the Flex coding hook. Defaults to 'erp' (Agave). */
  erpType?: 'flex' | 'erp';
}

const CodingEntityDropdown = <T extends CodingEntity>({
  entityType,
  selectedItem,
  onValueChange,
  label,
  placeholder,
  disabled,
  disableHeader = false,
  identifierField = 'name' as keyof T,
  dataDropdownType,
  cardGroupId,
  projectId,
  costCodeId,
  subJobId,
  locationId,
  departmentId,
  employeeId,
  emGroup,
  emCo,
  department,
  emCostCodeId,
  selectedGlCode,
  selectedProject,
  enabled = true,
  fetchOnlyOnOpen = false,
  includeDescription = false,
  overrideItems,
  erpType = 'erp',
}: CodingEntityDropdownProps<T>) => {
  const { t } = useTranslation();

  const { agaveAccount } = useAppSelector(selectAgaveSlice);
  const { selectedPaidol } = useAppSelector(selectUserCompanies);

  const isErp = erpType === 'erp';
  const isFlex = erpType === 'flex';
  // Both hooks are always called (rules of hooks) — only the active one is enabled
  const agaveHook = useAgaveCodingDropdown<T & AgaveEntity>({
    codingType: entityType,
    paidolId: selectedPaidol?.id || '',
    cardGroupId,
    projectId,
    costCodeId,
    subJobId,
    locationId,
    departmentId,
    employeeId,
    emGroup,
    emCo,
    department,
    emCostCodeId,
    enabled: isErp && !disabled && enabled,
    fetchOnlyOnOpen,
  });

  const FLEX_SUPPORTED_TYPES: EntityType[] = ['glCodes', 'projects', 'costCodes', 'costTypes', 'vendors'];
  const flexHook = useFlexCodingDropdown<T & FlexEntity>({
    codingType: entityType,
    paidolId: selectedPaidol?.id || '',
    cardGroupId,
    selectedGlCode,
    selectedProject,
    enabled: isFlex && !disabled && FLEX_SUPPORTED_TYPES.includes(entityType) && enabled,
  });

  const activeHook = isFlex ? flexHook : agaveHook;
  // SEE-1392: taken from flexHook directly, not from activeHook. Only the flex path
  // reports search failure; destructuring it from activeHook would force an equivalent
  // onto useAgaveCodingDropdown, which is a separate piece of work.
  const flexSearchFailed = isFlex && flexHook.searchFailed;
  const {
    currentItems,
    isSearching,
    isLoadingMore,
    hasMore,
    handleSearch,
    handleClearSearch,
    handleFocus,
    handleLoadMore,
    loadOptions,
  } = activeHook;

  // SEE-1030: after an Agave->direct swap the live `agaveAccount` is gone, so
  // fall back to the paidol's persisted source-system slug — a migrated QBO
  // paidol keeps rendering coding labels the QBO way (name only, no "- (number)"
  // suffix where the number is just the source_id). Non-QBO ERPs are unaffected.
  const sourceSystemSlug =
    agaveAccount?.source_system?.slug ?? selectedPaidol?.agaveSourceSystemSlug ?? undefined;
  const isQBDClient = useMemo(
    () => isErp && sourceSystemSlug === 'quick-books-desktop',
    [isErp, sourceSystemSlug]
  );
  const isQBOClient = useMemo(
    () => isErp && sourceSystemSlug === 'quick-books-online',
    [isErp, sourceSystemSlug]
  );

  const flexNameField = FLEX_NAME_FIELDS[entityType] || 'name';

  const baseGetOptionLabel = useGetOptionLabel({
    isQBDClient,
    isQBOClient,
    identifierField: isErp ? (identifierField as string) : null,
    // SEE-1325: the dropdown options need the same code-first format
    // `useCodingDisplay` applies to the selected value. Without this the list is
    // ordered by code but labelled name-first, which reads as unsorted.
    //
    // Gated on CODING_SORT_FIELDS — the map that answers "does the toggle
    // reorder this list?" — so the label flips only where the order does.
    // Locations have no entry: they have no code and are name-ordered in both
    // states, so their labels stay put too.
    identifierOrder: isErp
      ? Boolean(CODING_SORT_FIELDS[entityType]) &&
        isCodingSortedByCode(selectedPaidol, agaveAccount?.source_system?.slug)
        ? 'before'
        : defaultIdentifierOrder
      : null,
    nameField: isFlex ? flexNameField : 'name',
    includeDescription: isErp && includeDescription,
  });

  const getOptionLabel = useCallback(
    (option: any) => {
      if (isFlex && entityType === 'projects' && option) {
        const name = option.projectName ?? '';
        return option.projectCode ? `${name} (${option.projectCode})` : name;
      }
      return baseGetOptionLabel(option);
    },
    [isFlex, entityType, baseGetOptionLabel]
  );

  // Add selected item to current items if not already present
  const itemsSource = overrideItems ?? (currentItems as T[]);
  const itemsWithSelected = useMemo(() => {
    if (selectedItem && !itemsSource.find((item) => item.id === selectedItem.id)) {
      return [selectedItem, ...itemsSource];
    }
    return itemsSource;
  }, [itemsSource, selectedItem]);

  // SEE-1154: Sage Intacct cost codes (TASK) are project-scoped — the same code
  // (and same 1:1 AgaveCostCode id) has a different name per project. This
  // resolver (provided by the surrounding table/sidesheet/coding-sheet) yields
  // the selected project's name for a (projectId, costCodeId); overlay it onto
  // the option label so the selected value and the list read the project's own
  // name instead of the single collapsed AgaveCostCode.name. Additive: only
  // overrides when a per-project name exists.
  const getPerProjectCostCodeName = useContext(PerProjectCostCodeNameContext);

  // Convert items to TableDropdownOption format
  const tableDropdownOptions: TableDropdownOption<T>[] = useMemo(() => {
    if (!itemsWithSelected || !Array.isArray(itemsWithSelected)) return [];

    const overlayName = entityType === 'costCodes' && projectId ? getPerProjectCostCodeName : undefined;

    return itemsWithSelected.map((item) => {
      // Only cost codes reach the overlay (gated above), and those carry name;
      // read it defensively since the generic item type is the ERP-wide union.
      const currentName = (item as { name?: string | null }).name;
      const perProjectName = overlayName?.(projectId, item.id);
      const displayItem =
        perProjectName && currentName !== perProjectName
          ? { ...item, name: perProjectName, fullName: perProjectName.toLowerCase() }
          : item;
      return {
        id: item.id,
        label: getOptionLabel(displayItem),
        value: item,
      };
    });
  }, [itemsWithSelected, getOptionLabel, entityType, projectId, getPerProjectCostCodeName]);

  // Find the selected option in the new format
  const selectedOption = useMemo(() => {
    if (!selectedItem) return null;

    // Find the option in tableDropdownOptions that matches the selected value
    const foundOption = tableDropdownOptions.find((option) => option.value.id === selectedItem.id);

    // If not found in options, create a temporary option for the selected value
    if (!foundOption && selectedItem) {
      const itemWithName = selectedItem as { name?: string };
      const optionLabel = getOptionLabel(selectedItem) || itemWithName.name || 'Unknown';
      return {
        id: selectedItem.id,
        label: optionLabel,
        value: selectedItem,
      };
    }

    return foundOption || null;
  }, [selectedItem, tableDropdownOptions, getOptionLabel]);

  const handleSelect = (option: TableDropdownOption<unknown> | null) => {
    // Debug: track dropdown selections in dev environments
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.debug('[CodingEntityDropdown] select', {
        entityType,
        selectedId: option?.value && typeof option.value === 'object' ? (option.value as any).id : undefined,
        selectedLabel: option?.label,
      });
    }
    if (option) {
      onValueChange(option.value as T);
    } else {
      onValueChange(null);
    }
    // Blur the active element to close the dropdown
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  };

  // Map entityType to translation key (entityTypes are plural, translation keys are mostly singular)
  const translationKeyMap: Record<EntityType, string> = {
    glCodes: 'glCode',
    projects: 'project',
    costCodes: 'costCode',
    costTypes: 'costType',
    subJobs: 'subJob',
    vendors: 'vendor',
    locations: 'locations', // This one is plural in translations
    departments: 'departments', // This one is plural in translations
    subaccounts: 'subaccounts',
    employees: 'employee',
    // No transactionSidesheet.equipment string exists (same as subaccounts) —
    // callers pass `label` explicitly, so this entry only satisfies the
    // exhaustive Record<EntityType, string>.
    equipment: 'equipment',
    // Same as equipment — callers pass `label`, so these only satisfy the
    // exhaustive Record<EntityType, string>.
    emCostCodes: 'emCostCode',
    emCostTypes: 'emCostType',
  };

  // Label logic
  const translationKey = translationKeyMap[entityType];
  const displayLabel = label || t(`transactionSidesheet.${translationKey}`);
  const displayPlaceholder = placeholder || t('tableDropdown.unassigned', 'Unassigned');
  const dropdownTypePrefix = isFlex ? 'flex' : 'agave';

  return (
    <Box
      sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', width: '100%' }}
      onClick={(e) => e.stopPropagation()}
    >
      {!disableHeader && (
        <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>
          {displayLabel}
        </Typography>
      )}
      <TableDropdown
        options={tableDropdownOptions}
        value={selectedOption?.value || null}
        onSelect={handleSelect}
        disabled={disabled}
        loading={isSearching || isLoadingMore}
        error={flexSearchFailed}
        renderEmpty={
          flexSearchFailed
            ? () => (
                <Typography variant="body2" color="error">
                  {t('flexCodingSearchError', "We couldn't load these options.")}
                </Typography>
              )
            : undefined
        }
        placeholder={displayPlaceholder}
        searchable={!overrideItems}
        searchPlaceholder={t('tableDropdown.typeToSearch', 'Type to search...')}
        onSearch={handleSearch}
        searchDebounceMs={800}
        data-dropdown-type={dataDropdownType || `${dropdownTypePrefix}-${entityType}`}
        data-dropdown-type-input={`${dataDropdownType || `${dropdownTypePrefix}-${entityType}`}-input`}
        hasMore={hasMore}
        onLoadMore={handleLoadMore}
        isLoadingMore={isLoadingMore}
        onOpen={fetchOnlyOnOpen ? loadOptions : undefined}
        onFocus={handleFocus}
        onClose={handleClearSearch}
        // SEE-1834: ERP employee values are person names; other coding values stay visible.
        maskOptionLabels={entityType === 'employees'}
      />
    </Box>
  );
};

export default CodingEntityDropdown;
